/**
 * @fileoverview Input/output schemas for the story-image-prompt-creator-v2 agent.
 *
 * This agent generates image prompts from prose, identifying characters
 * and world elements present in the text.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.selectedText.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  shotFraming: z.string().describe('Cinematographer reasoning for how the shot is framed: camera distance, angle, character placement in frame, gaze directions, and composition choices — and why these serve the emotional beat of the scene.'),
  prompt: z.string().describe('The generated, optimized prompt for an image generation model.'),
  characterNames: z.array(z.string()).optional().describe('An array of exact names of characters identified in the prose.'),
  worldElementNames: z.array(z.string()).optional().describe('An array of exact names of world elements identified in the prose.'),
});

export type Output = z.infer<typeof OutputSchema>;
